/**
 * Hook for managing the domains list (selection, removal, search filter,
 * server-side persistence of selections).
 *
 * Extracted from useParsingWorkflow.ts (Phase 2 refactoring).
 * Owns: searchText state, computed `filteredDomains` and `selectedCount`.
 * Receives: [domains, setDomains] from the facade so domains stay shareable
 * across other workflow hooks.
 */

import { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from 'react'
import { message } from 'antd'
import api from '@/services/api'
import type { DomainWithAnalysis } from '../types'

interface UseDomainSelectionProps {
  domains: DomainWithAnalysis[]
  setDomains: Dispatch<SetStateAction<DomainWithAnalysis[]>>
  projectId?: number | null
}

export interface UseDomainSelectionResult {
  searchText: string
  setSearchText: Dispatch<SetStateAction<string>>
  filteredDomains: DomainWithAnalysis[]
  selectedCount: number
  toggleDomainSelection: (fullUrl: string) => void
  removeDomain: (fullUrl: string) => void
  saveSourceSelections: () => Promise<void>
}

export function useDomainSelection({
  domains,
  setDomains,
  projectId,
}: UseDomainSelectionProps): UseDomainSelectionResult {
  const [searchText, setSearchText] = useState('')

  const filteredDomains = useMemo(() => {
    if (!searchText) return domains
    const needle = searchText.toLowerCase()
    return domains.filter((d) => d.host.toLowerCase().includes(needle))
  }, [domains, searchText])

  const selectedCount = useMemo(
    () => domains.filter((d) => d.selected).length,
    [domains]
  )

  // Toggle domain selection by full_url (unique identifier)
  const toggleDomainSelection = useCallback(
    (fullUrl: string) => {
      setDomains((prev) =>
        prev.map((d) => (d.full_url === fullUrl ? { ...d, selected: !d.selected } : d))
      )
    },
    [setDomains]
  )

  // Remove domain by full_url (unique identifier)
  const removeDomain = useCallback(
    (fullUrl: string) => {
      setDomains((prev) => prev.filter((d) => d.full_url !== fullUrl))
      message.success('Источник удален')
    },
    [setDomains]
  )

  // Save source selections back to server (selected/pending status)
  const saveSourceSelections = useCallback(async () => {
    if (!projectId || domains.length === 0) {
      return
    }

    try {
      const domainsWithIds = domains.filter((d) => d.source_id !== undefined)
      if (domainsWithIds.length === 0) {
        return
      }

      const selectedIds = domainsWithIds
        .filter((d) => d.selected)
        .map((d) => d.source_id!)
      const nonSelectedIds = domainsWithIds
        .filter((d) => !d.selected)
        .map((d) => d.source_id!)

      if (selectedIds.length > 0) {
        await api.bulkUpdateParsedUrlStatus(selectedIds, 'selected')
      }

      if (nonSelectedIds.length > 0) {
        await api.bulkUpdateParsedUrlStatus(nonSelectedIds, 'pending')
      }

      message.success('Выбор источников сохранён')
    } catch (error) {
      console.error('Failed to save source selections:', error)
      message.error('Не удалось сохранить выбор источников')
      throw error
    }
  }, [projectId, domains])

  return {
    searchText,
    setSearchText,
    filteredDomains,
    selectedCount,
    toggleDomainSelection,
    removeDomain,
    saveSourceSelections,
  }
}
